import { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import i18n from '../../i18n';
import { useParams, useHistory } from 'react-router-dom';
import * as Sentry from '@sentry/browser';

import { setDocTitle, scrollTop } from '../../utils/utilities';
import { useGlobalContext } from '../../context/GlobalContext';
import useSongChangeMetronome from './components/plugins/MetronomePlugin/useSongChangeMetronome';
import { removeSongChords } from '../../utils/chord';
import { getSetting, setSetting, settingKeys } from '../../utils/settings';
import { incrementSongPlayCount, cleanupOldCooldowns, getPopularSongs } from '../../helpers/songStats';
import { transposeSong, getKeyAfterTranspose } from '../../utils/autoTranspose';
import { stopScroll } from '../../utils/autoScrollHelpers';
import useJumpToMyPart, { JUMP_TO_MY_PART } from '../../hooks/useJumpToMyPart';

import NoContent from '../../components/common/NoContent';
import auth from '../../services/authService';
import songService from '../../services/songService';
import serviceService from '../../services/serviceService';
import ViewHeader, { useViewHeaderStore } from './components/ViewHeader';
import ViewBody from './components/ViewBody';
import SuperScroll from './components/SuperScroll';
import SuperMetronome from './components/SuperMetronome';
import TimingRecorder from './components/TimingRecorder';
import { isCalibrationUser, TIMING_RECORDER_AVAILABLE } from '../../utils/timingRecorder';
import SmartScrollDebug from './components/SmartScrollDebug';
import AutoScrollStatus from './components/AutoScrollStatus';
import Footer from '../../components/common/Footer';
import PageBar from '../../components/PageBar';
import { ReactComponent as CreateIcon } from '../../icons/page-edit.svg';
import { ReactComponent as PuzzleIcon } from '../../icons/puzzle.svg';

import './View.scss';

const View = () => {
  const { t } = useTranslation();
  const [service, setService] = useState([]);
  const [currentSong, setCurrentSong] = useState({});
  const [songs, setSongs] = useState([]);
  const [popularityData, setPopularityData] = useState(null);
  const [lastSong, setLastSong] = useState(false);
  const [firstSong, setFirstSong] = useState(false);
  const [loadingSongs, setLoadingSongs] = useState(false);
  const [loadError, setLoadError] = useState(null);
  const [initialServiceFetch, setInitialServiceFetch] = useState(true);
  const params = useParams();
  const history = useHistory();

  const {
    scrollTimerRef,
    setIsScrolling,
    songs: allSongs,
    dataLoaded,
    showPlugins,
    handleShowPlugins,
  } = useGlobalContext();

  // To stop metronome on song changes
  const handleMetronomeOnSongChange = useSongChangeMetronome();

  useJumpToMyPart(songs, !loadingSongs);

  const [hideChords, setHideChords] = useState(() => Boolean(getSetting(settingKeys.hideChordsPlugin)));
  const [hidingChords, setHiddingChords] = useState(false);

  const [overview, setOverviewState] = useState(() => Boolean(getSetting(settingKeys.overviewPlugin)));

  // Overview can be turned off from two places — the plugin toggle and the header pill — and the
  // persisted setting has to move with the state either way, or the mode comes back on reload.
  const setOverview = useCallback((value) => {
    setSetting(settingKeys.overviewPlugin, value);
    setOverviewState(value);
  }, []);

  const [continuousView, setContinuousView] = useState(() => Boolean(getSetting('continuous-view-plugin/enabled')));

  // Track which songs have been manually reset to original (only for current viewing session)
  const [resetSongIds, setResetSongIds] = useState(new Set());

  // Store original song data before any transposes (for restoring on "Original Key" click)
  const [originalSongData, setOriginalSongData] = useState({});

  useEffect(() => {
    cleanupOldCooldowns();

    return () => {
      stopScroll(scrollTimerRef, setIsScrolling);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Helper function to apply auto-transpose if transposeBy property exists
  const applyAutoTranspose = useCallback(
    (song) => {
      // Check if this song has been manually reset in the current session
      if (resetSongIds.has(song._id)) {
        return song;
      }

      if (!song.transposeBy || song.transposeBy === 0 || song.transposeBy === '') {
        return song;
      }

      const semitones = typeof song.transposeBy === 'string' ? parseInt(song.transposeBy) : song.transposeBy;

      if (isNaN(semitones) || semitones === 0) {
        return song;
      }

      // Store the ORIGINAL song data before any transposes (deep clone to prevent mutations)
      setOriginalSongData((prev) => ({
        ...prev,
        [song._id]: JSON.parse(JSON.stringify(song)),
      }));

      // Store original key for reset functionality
      const originalKey = song.key;

      // Transpose the song
      const transposedSongData = transposeSong(song, semitones);

      // Update the displayed key
      const newKey = getKeyAfterTranspose(originalKey, semitones);
      transposedSongData.key = newKey;

      // Mark song as auto-transposed and store original key
      transposedSongData.isAutoTransposed = true;
      transposedSongData.originalKey = originalKey;
      transposedSongData.autoTransposeSemitones = semitones;

      return transposedSongData;
    },
    [resetSongIds],
  );

  // Handle manual transpose from plugin - transpose song data before rendering
  const handleManualTranspose = useCallback(
    (semitones, songIndex = null) => {
      const transposeSongData = (song) => {
        // Preserve the very first pre-transpose key/data so "Original Key" always
        // points back to where the song started, even across multiple manual clicks
        const originalKey = song.isAutoTransposed && song.originalKey ? song.originalKey : song.key;

        // Accumulate the running offset from the original key. We can't recompute it
        // from the keys themselves because calculateSemitones can't resolve
        // single-notation accidentals (e.g. 'A#', 'C#'), so it would report 0.
        const previousSemitones = song.autoTransposeSemitones || 0;

        if (!originalSongData[song._id]) {
          setOriginalSongData((prev) => ({
            ...prev,
            [song._id]: JSON.parse(JSON.stringify(song)),
          }));
        }

        const transposed = transposeSong(song, semitones);

        transposed.key = getKeyAfterTranspose(song.key, semitones);
        transposed.isAutoTransposed = true;
        transposed.originalKey = originalKey;
        transposed.autoTransposeSemitones = previousSemitones + semitones;

        return transposed;
      };

      if (params.service) {
        // Transpose specific song in service or first song if no index provided
        const targetIndex = songIndex !== null ? songIndex : 0;

        setSongs((prevSongs) => {
          const updatedSongs = [...prevSongs];

          if (updatedSongs[targetIndex]) {
            updatedSongs[targetIndex] = transposeSongData(updatedSongs[targetIndex]);
          }
          return updatedSongs;
        });
      } else {
        // Single song view
        setCurrentSong((prevSong) => transposeSongData(prevSong));
      }
    },
    [params.service, originalSongData],
  );

  // Reset auto-transpose - restore original song data
  const resetAutoTranspose = useCallback(
    (songId) => {
      const originalSong = originalSongData[songId];

      if (!originalSong) {
        return;
      }

      const restoredSong = JSON.parse(JSON.stringify(originalSong));
      setResetSongIds((prev) => new Set(prev).add(songId));

      if (params.service) {
        setSongs((prevSongs) => prevSongs.map((s) => (s._id === songId ? restoredSong : s)));

        // Only update global store for first song (others use local transposedKeys)
        const songIndex = songs.findIndex((s) => s._id === songId);
        if (songIndex === 0) {
          useViewHeaderStore.getState().setSongKey(originalSong.key);
        }
      } else {
        setCurrentSong(restoredSong);
        useViewHeaderStore.getState().setSongKey(originalSong.key);
      }
    },
    [originalSongData, params.service, songs],
  );

  // Handle toggling chord visibility
  // Deep copy songs to preserve original data structure and avoid mutation
  // This allows toggling between showing/hiding chords without data loss
  useEffect(() => {
    const songsCopy = JSON.parse(JSON.stringify(songs));

    if (hideChords && !hidingChords) {
      if (params.service) {
        if (songs.length > 0) {
          songsCopy.forEach((song) => {
            // Store original parts before removing chords so they can be restored
            if (!song.originalParts) {
              song.originalParts = JSON.parse(JSON.stringify(song.parts));
              song.parts = removeSongChords(song);
            }
          });

          setHiddingChords(true);
          setSongs(songsCopy);
        }
      } else if (currentSong?._id) {
        const songCopy = JSON.parse(JSON.stringify(currentSong));

        if (!songCopy.originalParts) {
          songCopy.originalParts = JSON.parse(JSON.stringify(songCopy.parts));
          songCopy.parts = removeSongChords(songCopy);
        }

        setHiddingChords(true);
        setCurrentSong(songCopy);
      }
    } else if (!hideChords && hidingChords) {
      if (params.service) {
        songsCopy.forEach((song) => {
          if (song.originalParts) {
            song.parts = JSON.parse(JSON.stringify(song.originalParts));
            delete song.originalParts;
          }
        });

        setSongs(songsCopy);
        setHiddingChords(false);
      } else {
        if (currentSong) {
          const songCopy = JSON.parse(JSON.stringify(currentSong));

          if (songCopy.originalParts) {
            songCopy.parts = JSON.parse(JSON.stringify(songCopy.originalParts));
            delete songCopy.originalParts;
          }
          setCurrentSong(songCopy);
          setHiddingChords(false);
        }
      }
    }
  }, [hideChords, hidingChords, songs, params.service, currentSong]);

  useEffect(() => {
    // Playing a service
    if (params.service) {
      const fetchService = async () => {
        let service = [];
        const songs = [];

        if (initialServiceFetch) {
          setInitialServiceFetch(false);

          try {
            setLoadingSongs(true);
            setLoadError(null);

            const { data } = await serviceService.getService(params.id);
            service = data;

            // Check if coming from editing (to skip play count increment)
            const urlParams = new URLSearchParams(history.location.search);
            const isFromEditing = urlParams.get('fromEdit') === 'true';

            // Load all songs in parallel for better performance
            const songPromises = service.songsIds.map(async (songId) => {
              let song = await songService.getSong(songId);
              song.parts = songService.loadDuplicateParts(song);

              // Apply auto-transpose if transposeBy property exists
              song = applyAutoTranspose(song);

              // Remove chords from song if necessary
              if (getSetting(settingKeys.hideChordsPlugin)) {
                song.originalParts = JSON.parse(JSON.stringify(song.parts));
                song.parts = removeSongChords(song);
                setHiddingChords(true);
              }

              return { songId, song };
            });

            // Wait for all songs to load
            const songResults = await Promise.all(songPromises);
            const loadedSongs = songResults.map(({ song }) => song);

            songs.push(...loadedSongs);

            // Update play counts in parallel
            /* Only increment play count if the user viewing the song belongs
              to the same team as the song, and if not coming from editing */
            if (!isFromEditing) {
              const currentUser = auth.getCurrentUser();
              const playCountPromises = songResults
                .filter(({ song }) => currentUser && song.team._id === currentUser.teamId)
                .map(({ songId }) => incrementSongPlayCount(songId, false));
              // Fire and forget - don't wait for these to complete
              Promise.all(playCountPromises).catch(() => {
                // Silently handle any play count update failures
              });
            }

            setService(service);
            setSongs(songs);
            setDocTitle(i18n.t('view.serviceDocTitle', { title: service.title }));

            // service has 0 songs
            if (service?.songsIds?.length === 0) return history.replace('/not-found');

            // disable next button for services with 1 song
            if (service?.songsIds?.length === 1) {
              setLastSong(true);
              setFirstSong(true);
            }

            setLoadingSongs(false);
          } catch (ex) {
            setLoadingSongs(false);
            handleException(ex);
          }
        }
      };
      fetchService();
    } else {
      // Playing a single song
      const fetchSong = async () => {
        try {
          setLoadingSongs(true);
          setLoadError(null);

          let song = await songService.getSong(params.id);
          song.parts = songService.loadDuplicateParts(song);

          // Apply auto-transpose if transposeBy property exists
          song = applyAutoTranspose(song);

          // Remove chords from song if necessary
          if (getSetting(settingKeys.hideChordsPlugin)) {
            song.originalParts = JSON.parse(JSON.stringify(song.parts));
            song.parts = removeSongChords(song);
            setHiddingChords(true);
          }

          const urlParams = new URLSearchParams(history.location.search);
          const isFromEditing = urlParams.get('fromEdit') === 'true';
          const currentUser = auth.getCurrentUser();

          /* Only increment play count if the user viewing the song belongs
            to the same team as the song, and if not coming from editing */
          if (currentUser && song.team._id === currentUser.teamId) {
            await incrementSongPlayCount(song._id, isFromEditing);
          }

          // not a service, hide service controls
          setService(null);

          setCurrentSong(song);

          setDocTitle(`${song.title} - ${song.artist}`);

          setLoadingSongs(false);
        } catch (ex) {
          setLoadingSongs(false);
          handleException(ex);
        }
      };
      fetchSong();
    }

    // Anything that isn't a missing song used to end here silently, leaving an empty screen with
    // no way to tell a slow load from a failed one. A 403 and a bug in the data both land here.
    const handleException = (ex) => {
      if (ex.response?.status === 404 || ex.response?.status === 400) {
        history.replace('/not-found');
        return;
      }

      const status = ex.response?.status;
      setLoadError(status === 403 ? i18n.t('view.noAccess') : i18n.t('view.loadFailed'));

      Sentry.captureException(ex, {
        tags: { section: 'view-load', service: Boolean(params.service) },
        extra: { id: params.id, status },
      });
    };
    // Depend on the primitive route params (params.id/params.service) rather than the
    // `params` object: react-router v5 returns a new `match.params` reference on every
    // re-render (e.g. when GlobalContext changes on auto-scroll start), which would
    // otherwise re-run this effect and re-fetch the song, wiping any manual transpose.
  }, [params.id, params.service, history, initialServiceFetch, applyAutoTranspose]);

  // Set current song
  useEffect(() => {
    let hash = history.location.hash;
    let songIndex = null;

    if (!hash) {
      // the first song in the service
      songIndex = 0;
    } else {
      // hash is not 0 based
      songIndex = parseInt(hash.substring(1)) - 1;
    }

    if (songIndex >= 0 && songs[songIndex]) {
      // In continuous view mode, always show the first song in the header
      if (continuousView) {
        setCurrentSong(songs[0]);
        setFirstSong(true);
        setLastSong(songs.length === 1);
      } else {
        // Normal view: set the current song based on hash
        setCurrentSong(songs[songIndex]);

        // check ahead to handle previous and next buttons
        !songs[songIndex - 1] ? setFirstSong(true) : setFirstSong(false);
        !songs[songIndex + 1] ? setLastSong(true) : setLastSong(false);
      }
    } else {
      // set current song to the first song in the service
      setCurrentSong(songs[0]);
      setFirstSong(true);
      setLastSong(false);
    }
  }, [songs, history.location.hash, continuousView]);

  // In continuous view, scroll to the song section that matches the hash.
  // Keyed on the hash + song count (not the `songs` array reference) so that
  // in-place content updates like transpose or hiding chords don't re-trigger
  // this and yank the page back to the hashed song's top.
  useEffect(() => {
    if (!continuousView) return;

    const hash = history.location.hash;
    if (!hash) return;

    // useJumpToMyPart takes the page further, to a part inside that song
    if (history.location.state?.[JUMP_TO_MY_PART]) return;

    const songIndex = parseInt(hash.substring(1)) - 1;
    if (songIndex < 0 || songIndex >= songs.length) return;

    // Use setTimeout to ensure the DOM is fully rendered
    const timer = setTimeout(() => {
      const targetSection = document.getElementById(`song-section-${songIndex}`);
      if (targetSection) {
        const headerOffset = -66; // Account for sticky header
        const elementPosition = targetSection.getBoundingClientRect().top;
        const offsetPosition = elementPosition + window.pageYOffset - headerOffset;

        window.scrollTo({
          top: offsetPosition,
          behavior: 'smooth',
        });
      }
    }, 100);

    return () => clearTimeout(timer);
    // Intentionally depend on songs.length (not songs) so transposing a song,
    // which replaces the array, doesn't force a scroll back to the hash.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [history.location.hash, continuousView, songs.length]);

  const stopAutoScrollOnSongChange = () => {
    stopScroll(scrollTimerRef, setIsScrolling);
  };

  const stopPluginsOnSongChange = (newSong) => {
    stopAutoScrollOnSongChange();
    handleMetronomeOnSongChange(newSong);
  };

  const handlePreviousSong = () => {
    const index = songs.findIndex((s) => s._id === currentSong._id);

    let previousSong = null;
    previousSong = songs[index - 1];

    // check for first song
    if (!previousSong) return null;

    // hash is not 0 based, no need to subtract 1
    history.push(`#${index}`);

    setCurrentSong(previousSong);
    setLastSong(false);

    stopPluginsOnSongChange(previousSong);
    scrollTop(undefined, false);

    // next song, first song
    if (!songs[index - 2]) {
      setFirstSong(true);
      return null;
    }
  };

  const handleNextSong = () => {
    const index = songs.findIndex((s) => s._id === currentSong._id);

    let nextSong = null;
    nextSong = songs[index + 1];

    // check for last song
    if (!nextSong) return null;

    // index is 0 based, add 2 (Hash is not 0 based)
    history.push(`#${index + 2}`);

    setCurrentSong(nextSong);
    setFirstSong(false);

    stopPluginsOnSongChange(nextSong);
    scrollTop(undefined, false);

    // next song, last song
    if (!songs[index + 2]) {
      setLastSong(true);
      return null;
    }
  };

  // Calculate popularity data from GlobalContext songs (no need to fetch again!)
  // Only set popularityData if GlobalContext has loaded data - otherwise SongBadges will use fallback logic
  useEffect(() => {
    if (dataLoaded && allSongs && allSongs.length > 0) {
      const { maxPlays, popularSongs, mostPlayedOnly } = getPopularSongs(allSongs);
      setPopularityData({ maxPlays, popularSongs, mostPlayedOnly });
    } else {
      // Clear popularityData if GlobalContext hasn't loaded yet (e.g., after page refresh)
      setPopularityData(null);
    }
  }, [allSongs, dataLoaded]);

  // Calculate current song index for transpose functionality
  const currentSongIndex = currentSong?._id ? songs.findIndex((s) => s._id === currentSong._id) : -1;

  // The setting only applies inside a service; a song opened on its own is always viewed alone.
  // Everything below reads this rather than the setting, so the bar heads the first song like the
  // bar over each song after it.
  const continuous = Boolean(continuousView && service && songs.length > 0);
  const user = auth.getCurrentUser();
  const canEditSong = Boolean(currentSong?._id && user?.isAdmin && user.teamId === currentSong.team?._id);

  const handleEditSong = () => {
    const from = service?._id ? `/view/service/${service._id}${history.location.hash}` : `/view/${currentSong._id}`;
    history.push({ pathname: `/songs/${currentSong._id}`, state: { from } });
  };

  return (
    <>
      <PageBar
        back={{ label: t('common.back'), icon: true, onClick: () => history.push('/songs') }}
        title={continuous && currentSong?.title ? `#1 ${currentSong.title}` : currentSong?.title}
        subtitle={currentSong?.artist}
        actions={[
          ...(canEditSong ? [{ label: t('view.editSong'), icon: <CreateIcon />, onClick: handleEditSong }] : []),
          { label: t('view.plugins'), icon: <PuzzleIcon />, pressed: showPlugins, onClick: handleShowPlugins },
        ]}
      />
      <div className="View mg-container">
        <div className="mg-row">
          <div className="mg-col">
            <div className="view-song">
              {service?.length === 0 || !currentSong?._id ? (
                <NoContent loading={loadingSongs} message={loadError} />
              ) : (
                <>
                  <ViewHeader
                    song={currentSong}
                    songs={allSongs}
                    serviceSongs={songs}
                    popularityData={popularityData}
                    service={service}
                    onPrevious={handlePreviousSong}
                    onNext={handleNextSong}
                    firstSong={firstSong}
                    lastSong={lastSong}
                    hideChords={hideChords}
                    setHideChords={setHideChords}
                    overview={overview}
                    setOverview={setOverview}
                    continuousView={continuous}
                    setContinuousView={setContinuousView}
                    resetAutoTranspose={resetAutoTranspose}
                    onManualTranspose={handleManualTranspose}
                    currentSongIndex={currentSongIndex}
                  />
                  <ViewBody
                    song={currentSong}
                    songs={songs}
                    service={service}
                    onPrevious={handlePreviousSong}
                    onNext={handleNextSong}
                    continuousView={continuous}
                    resetAutoTranspose={resetAutoTranspose}
                    onManualTranspose={handleManualTranspose}
                    currentSongIndex={currentSongIndex}
                    hideChords={hideChords}
                    overview={overview}
                  />
                </>
              )}
            </div>
          </div>
        </div>
      </div>
      <Footer marginBottom={7} />
      <AutoScrollStatus />
      {TIMING_RECORDER_AVAILABLE && isCalibrationUser() && currentSong?._id && (
        // Calibration only, and it renders nothing unless the Part Timer setting is on. Same song
        // list ViewBody schedules from, so its parts line up with the schedule's breakpoints.
        // The constant is checked here as well as inside so a production build drops the whole
        // subtree rather than mounting a component that will only return null, and the admin check
        // sits beside it so a non-admin never mounts either instrument.
        <>
          <TimingRecorder
            songs={continuous ? songs : [currentSong]}
            song={currentSong}
            overview={overview}
            setOverview={setOverview}
          />
          {/* Same gate and the same song list: the x-ray draws the schedule those parts are being
              scrolled by, and is switched on separately in the Auto Scroll settings modal */}
          <SmartScrollDebug songs={continuous ? songs : [currentSong]} />
        </>
      )}
      {currentSong?._id && (getSetting(settingKeys.superMetronome) || getSetting(settingKeys.superScroll)) && (
        <div className="SuperControls">
          <SuperMetronome song={currentSong} />
          <SuperScroll song={currentSong} continuousView={continuous} />
        </div>
      )}
    </>
  );
};

export default View;
